import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import { SUPPORTED_LANGUAGES } from '../../i18n/translations';
import api from '../../services/api';

const LANGUAGE_MAP = {
  en: 'English',
  ru: 'Russian',
  zh: 'Chinese',
  tr: 'Turkish',
  ar: 'Arabic',
};

/**
 * Owns the message draft locally so keystrokes do not re-render the full history list.
 */
function SupportMessageComposer({
  isAdmin = false,
  connected = false,
  uploadingFile = false,
  selectedFile = null,
  replyToMessage = null,
  onClearReply,
  onSend,
  onPickFile,
  insertRequest = null,
}) {
  const { t } = useLanguage();
  const [newMessage, setNewMessage] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [showTranslationPreview, setShowTranslationPreview] = useState(false);
  const [selectedTranslationLanguage, setSelectedTranslationLanguage] = useState('English');
  const [translatingText, setTranslatingText] = useState(false);
  const [translateError, setTranslateError] = useState('');
  const inputRef = useRef(null);
  const wantFocusRef = useRef(false);

  const translationLanguages = useMemo(
    () => SUPPORTED_LANGUAGES.filter((lang) => lang.code !== 'ru').map((lang) => ({
      code: lang.code,
      name: lang.name,
      flag: lang.flag,
      gptName: LANGUAGE_MAP[lang.code],
    })),
    [],
  );

  const focusInput = () => {
    requestAnimationFrame(() => {
      inputRef.current?.focus?.();
    });
  };

  useEffect(() => {
    if (!insertRequest || insertRequest.text == null) return;
    setNewMessage(insertRequest.text);
    setShowTranslationPreview(false);
    setTranslatedText('');
    setTranslateError('');
    wantFocusRef.current = true;
    focusInput();
  }, [insertRequest]);

  // Не disabled={!connected}: краткий reconnect WS снимал фокус — приходилось кликать снова.
  useEffect(() => {
    if (uploadingFile) return undefined;
    if (!wantFocusRef.current) return undefined;
    focusInput();
    return undefined;
  }, [uploadingFile, connected]);

  const clearDraft = () => {
    setNewMessage('');
    setTranslatedText('');
    setShowTranslationPreview(false);
    setTranslateError('');
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    if (showTranslationPreview || translatedText) {
      setShowTranslationPreview(false);
      setTranslatedText('');
    }
    if (translateError) setTranslateError('');
  };

  const handleTranslateText = async () => {
    const text = newMessage.trim();
    if (!text || !isAdmin) return;
    try {
      setTranslatingText(true);
      setTranslateError('');
      const response = await api.translateText(text, selectedTranslationLanguage);
      if (response.data?.translated_text) {
        setTranslatedText(response.data.translated_text);
        setShowTranslationPreview(true);
      }
    } catch (err) {
      setTranslateError(err.data?.errorText || err.message || 'Ошибка перевода');
    } finally {
      setTranslatingText(false);
      wantFocusRef.current = true;
      focusInput();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if ((!text && !selectedFile) || !connected || uploadingFile) return;
    const ok = await onSend({
      text,
      translatedText,
      showTranslationPreview,
    });
    if (ok !== false) {
      clearDraft();
    }
    wantFocusRef.current = true;
    focusInput();
  };

  const selectedLangLabel = translationLanguages.find(
    (l) => l.gptName === selectedTranslationLanguage,
  )?.name || selectedTranslationLanguage;

  return (
    <Form onSubmit={handleSubmit}>
      {replyToMessage && (
        <div
          className="mb-2 p-2 rounded d-flex justify-content-between align-items-start"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            border: '1px solid var(--border-color)',
            color: 'var(--text-primary)',
          }}
        >
          <div className="small flex-grow-1">
            <span className="fw-bold">Ответ на: </span>
            <span className="text-muted">{replyToMessage.sender_name}</span>
            <div className="mt-1" style={{ fontSize: '0.9em', opacity: 0.9 }}>
              {(replyToMessage.content || '').slice(0, 100)}
              {(replyToMessage.content || '').length > 100 ? '…' : ''}
            </div>
          </div>
          <Button
            variant="link"
            size="sm"
            className="text-secondary p-0 ms-2"
            onClick={onClearReply}
            title="Отменить ответ"
          >
            <i className="bi bi-x-lg" />
          </Button>
        </div>
      )}

      {isAdmin && showTranslationPreview && translatedText && (
        <div
          className="mb-2 p-2 rounded border support-translation-preview"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)',
          }}
        >
          <div
            className="small mb-1 fw-bold support-translation-preview-text"
            style={{ color: 'var(--text-primary) !important' }}
          >
            <i className="bi bi-translate me-1" />
            Перевод на {selectedLangLabel}:
          </div>
          <div
            className="mb-2 support-translation-preview-text"
            style={{ color: 'var(--text-primary) !important' }}
          >
            {translatedText}
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setNewMessage(translatedText);
                setShowTranslationPreview(false);
                setTranslatedText('');
                wantFocusRef.current = true;
                focusInput();
              }}
            >
              Использовать перевод
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setShowTranslationPreview(false);
                setTranslatedText('');
                wantFocusRef.current = true;
                focusInput();
              }}
            >
              Отмена
            </Button>
          </div>
        </div>
      )}

      {isAdmin && newMessage.trim() && (
        <div className="mb-2 d-flex align-items-center gap-2 flex-wrap">
          <span className="small support-translation-label" style={{ color: 'var(--text-primary)' }}>
            <i className="bi bi-translate me-1" />
            Перевести на:
          </span>
          <div className="btn-group btn-group-sm" role="group">
            {translationLanguages.map((lang) => (
              <Button
                key={lang.code}
                variant={selectedTranslationLanguage === lang.gptName ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setSelectedTranslationLanguage(lang.gptName)}
                title={lang.name}
              >
                <span className="me-1">{lang.flag}</span>
                <span className="d-none d-sm-inline">{lang.name}</span>
              </Button>
            ))}
          </div>
          <Button
            variant="outline-info"
            size="sm"
            onClick={handleTranslateText}
            disabled={!connected || uploadingFile || translatingText}
            title={`Перевести на ${selectedLangLabel}`}
          >
            {translatingText ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Перевод...
              </>
            ) : (
              <>
                <i className="bi bi-translate me-1" />
                <span className="d-none d-sm-inline">Перевести</span>
              </>
            )}
          </Button>
        </div>
      )}

      {translateError && (
        <div className="small text-danger mb-2">{translateError}</div>
      )}

      {!connected && (
        <div className="small text-muted mb-1">
          <Spinner animation="border" size="sm" className="me-1" />
          Переподключение… можно продолжать писать
        </div>
      )}

      <InputGroup>
        <Button
          variant="outline-secondary"
          onClick={onPickFile}
          disabled={uploadingFile}
          type="button"
        >
          <i className="bi bi-paperclip" />
        </Button>
        <Form.Control
          ref={inputRef}
          type="text"
          placeholder={t('support.enterMessage')}
          value={newMessage}
          onChange={handleInputChange}
          onFocus={() => {
            wantFocusRef.current = true;
          }}
          onBlur={(e) => {
            // Если фокус ушёл на кнопку внутри формы — не считаем, что пользователь «ушёл» из композера
            const next = e.relatedTarget;
            if (next && e.currentTarget.closest('form')?.contains(next)) {
              wantFocusRef.current = true;
              return;
            }
            wantFocusRef.current = false;
          }}
        />
        <Button
          variant="primary"
          type="submit"
          disabled={(!newMessage.trim() && !selectedFile) || !connected || uploadingFile}
        >
          {uploadingFile ? <Spinner animation="border" size="sm" /> : t('support.send')}
        </Button>
      </InputGroup>
    </Form>
  );
}

export default React.memo(SupportMessageComposer);
